import { Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';

const PASOS_PROGRESO = 20;

@Injectable({
  providedIn: 'root',
})
export class LoadingService {
  readonly isLoading = signal<boolean>(false);
  readonly progress = signal<number>(0);
  readonly isProgressComplete = signal<boolean>(false);
  readonly isFullyLoaded = signal<boolean>(true);
  // Solo lo activan las llamadas puntuales que lo pidan (ver mostrarCarga); el splash
  // de home nunca lo toca y se ve exactamente igual que siempre.
  readonly fondoOscuro = signal<boolean>(false);
  readonly loadingFinished$ = new Subject<void>();

  private hasRunHomeLoading = false;
  private progressIntervalId: ReturnType<typeof setInterval> | null = null;
  private completeTimeoutId: ReturnType<typeof setTimeout> | null = null;

  startHomeLoading(): void {
    if (this.hasRunHomeLoading) {
      this.isFullyLoaded.set(true);
      return;
    }

    this.hasRunHomeLoading = true;
    this.fondoOscuro.set(false);
    void this.iniciarCarga(5000);
  }

  // Reutiliza la misma pantalla (logo + barra) para transiciones puntuales del flujo,
  // sin el bloqueo de "una sola vez por sesión" que tiene el splash de home.
  mostrarCarga(duracionMs = 1500, opciones?: { fondoOscuro?: boolean }): Promise<void> {
    this.fondoOscuro.set(opciones?.fondoOscuro ?? false);
    return this.iniciarCarga(duracionMs);
  }

  cancelLoading(): void {
    if (this.progressIntervalId) {
      clearInterval(this.progressIntervalId);
    }
    if (this.completeTimeoutId) {
      clearTimeout(this.completeTimeoutId);
    }
    document.body.style.overflow = '';
  }

  private iniciarCarga(duracionMs: number): Promise<void> {
    this.isLoading.set(true);
    this.progress.set(0);
    this.isProgressComplete.set(false);
    this.isFullyLoaded.set(false);
    document.body.style.overflow = 'hidden';

    const intervaloMs = duracionMs / PASOS_PROGRESO;
    let paso = 0;
    this.progressIntervalId = setInterval(() => {
      paso++;
      this.progress.set(Math.min(100, Math.round((paso / PASOS_PROGRESO) * 100)));
    }, intervaloMs);

    return new Promise((resolve) => {
      this.completeTimeoutId = setTimeout(() => this.completarProgreso(resolve), duracionMs);
    });
  }

  private completarProgreso(resolve: () => void): void {
    if (this.progressIntervalId) {
      clearInterval(this.progressIntervalId);
    }
    this.progress.set(100);
    this.isProgressComplete.set(true);

    setTimeout(() => this.ocultarPantalla(resolve), 500);
  }

  private ocultarPantalla(resolve: () => void): void {
    this.isLoading.set(false);

    setTimeout(() => this.finalizarCarga(resolve), 500);
  }

  private finalizarCarga(resolve: () => void): void {
    this.isFullyLoaded.set(true);
    document.body.style.overflow = '';
    this.loadingFinished$.next();
    resolve();
  }
}
